/* eslint-disable @typescript-eslint/no-explicit-any */
import { get, put } from "@/shared/utils/fetchApi";

export interface TourConfig {
  version: string;
  initialModal: {
    title: {
      pt: string;
      en: string;
    };
    contentHtml: {
      pt: string;
      en: string;
    };
    buttons: {
      start: {
        pt: string;
        en: string;
      };
      skip: {
        pt: string;
        en: string;
      };
    };
  };
  steps: Array<{
    id: string;
    name: {
      pt: string;
      en: string;
    };
    description: {
      pt: string;
      en: string;
    };
    objective: {
      pt: string;
      en: string;
    };
    route: string;
    targetElement: string;
    validatorKey: string;
    order: number;
    enabled: boolean;
  }>;
}

export interface SettingItem {
  id: string;
  type: string;
  fields: Record<string, any>;
  value: any; // ✅ Campo correto retornado pelo backend
  secure: number;
  created_at: string;
  updated_at: string;
}

export class SettingsService {
  /**
   * Busca as configurações do tour do backend
   */
  static async getTourSettings(): Promise<TourConfig> {
    // v2 dropa envelope: GET /settings?type=tour_settings retorna SettingItem[] direto.
    // legacy: { success, data: SettingItem[] }.
    const items = await get<SettingItem[]>("/settings?type=tour_settings");

    if (Array.isArray(items)) {
      const tourSetting = items.find((item) => item.type === "tour_settings");
      if (tourSetting && tourSetting.value) {
        return tourSetting.value as TourConfig;
      }
    }

    throw new Error("Failed to load tour settings");
  }

  /**
   * Busca uma configuração específica por tipo
   */
  static async getSettingByType<T = any>(type: string): Promise<T> {
    // v2 dropa envelope: GET /settings retorna SettingItem[] direto (filtra client-side).
    const items = await get<SettingItem[]>(`/settings?type=${type}`);

    if (Array.isArray(items)) {
      const setting = items.find((item) => item.type === type);
      if (setting && setting.value) {
        return setting.value as T;
      }
    }

    throw new Error(`Failed to load setting: ${type}`);
  }

  /**
   * Busca todas as configurações públicas (secure = 0)
   */
  static async getAllSettings(): Promise<SettingItem[]> {
    return (await get<SettingItem[]>("/settings")) ?? [];
  }

  /**
   * Busca o registro bruto de settings (incluindo id, fields e value) por tipo
   */
  static async getRawSettingByType(type: string): Promise<SettingItem | null> {
    const items = await get<SettingItem[]>(`/settings?type=${type}`);

    if (Array.isArray(items)) {
      const setting = items.find((item) => item.type === type);
      return setting || null;
    }

    return null;
  }

  /**
   * Atualiza uma configuração existente
   */
  static async updateSetting(
    id: string,
    payload: Partial<Pick<SettingItem, "fields" | "value" | "secure">>,
  ): Promise<SettingItem> {
    // PUT /settings/:id retorna SettingItem flat (exige role admin/superadmin).

    return await put<SettingItem>(`/settings/${id}`, payload);
  }
}
